import 'core-js/modules/web.dom-collections.iterator.js';
import * as React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { rem } from 'polished';
import { useField } from 'formik';
import { selectFromTheme } from '@thisisbud/sc-utils';
import { inputLabelClassName, inputHintClassName, inputErrorClassName } from '@thisisbud/bui-react-typography';
import { jsxs, jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t;
const inputClassName = 'bui-input';
const inputFieldClassName = `${inputClassName}__field`;
const inputPrefixClassName = `${inputClassName}__prefix`;
const inputTextPrefixWrapperClassName = `${inputClassName}__text-prefix-wrapper`;
const inputErrorStateClassName = `${inputClassName}--error`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledWrapper",
  componentId: "sc-1gok88p-0"
})(_t || (_t = _`
  position: relative;

  * + & {
    margin-top: ${0};
  }

  .${0} + &,
  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }

  .${0} {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    padding-left: ${0};
    padding-right: ${0};
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    color: ${0};
    letter-spacing: ${0};
    line-height: ${0};
    pointer-events: none;
    z-index: 2;
  }

  .${0} {
    display: block;
    padding: ${0};
    padding-left: 0;
    padding-right: 0;
  }

  .${0} {
    position: relative;
    display: block;
    width: 100%;
    padding: ${0};
    box-sizing: border-box;
    border-style: solid;
    border-color: ${0};
    appearance: none;
    background-color: ${0};
    border-radius: ${0};
    border-width: ${0};
    outline: ${0} solid ${0}00;
    outline-offset: ${0};
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    color: ${0};
    letter-spacing: ${0};
    line-height: ${0};
    z-index: 1;
    transition: border-color 0.2s ease-in-out, outline 0.2s ease-in-out;

    &:focus {
      outline: ${0} solid ${0};
    }

    &:disabled {
      background-color: ${0};
      border-color: ${0};
      color: ${0};

      & ~ .${0} {
        color: ${0};
      }
    }
  }

  &.${0} {
    .${0} {
      background-color: ${0};
      border-color: ${0};
      color: ${0};

      :focus {
        outline: ${0} solid ${0};
      }
    }
  }
`), selectFromTheme('spacingXXSmallDefault'), inputLabelClassName, inputHintClassName, inputErrorClassName, selectFromTheme('spacingXXXSmallDefault'), inputPrefixClassName, selectFromTheme('paddingInputHorizontalDefault'), selectFromTheme('spacingXXXXSmallDefault'), selectFromTheme('fontFamilyInput'), selectFromTheme('fontSizeInputDefault'), selectFromTheme('fontWeightInput'), selectFromTheme('colorTextInputDefault'), selectFromTheme('letterSpacingInputDefault'), selectFromTheme('lineHeightInputDefault'), inputTextPrefixWrapperClassName, selectFromTheme('paddingInputDefault'), inputFieldClassName, selectFromTheme('paddingInputDefault'), selectFromTheme('colorBorderInputDefault'), selectFromTheme('colorBackgroundInputDefault'), selectFromTheme('borderRadiusInputDefault'), selectFromTheme('borderWidthInputDefault'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('colorTextPrimaryAccent'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('fontFamilyInput'), selectFromTheme('fontSizeInputDefault'), selectFromTheme('fontWeightInput'), selectFromTheme('colorTextInputDefault'), selectFromTheme('letterSpacingInputDefault'), selectFromTheme('lineHeightInputDefault'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('colorTextPrimaryAccent'), selectFromTheme('colorBackgroundInputDisabled'), selectFromTheme('colorBorderInputDisabled'), selectFromTheme('colorTextInputDisabled'), inputPrefixClassName, selectFromTheme('colorTextInputDisabled'), inputErrorStateClassName, inputFieldClassName, selectFromTheme('colorBackgroundInputError'), selectFromTheme('colorBorderInputError'), selectFromTheme('colorTextInputError'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('colorBorderInputError'));

/**
 * Get the default input mode based on input type.
 *
 * @param inputType - The type of input field
 */
function getDefaultInputMode(inputType) {
  switch (inputType) {
    case 'number':
      return 'numeric';
    case 'password':
      return 'text';
    default:
      return inputType;
  }
}

/**
 * Renders an input icon component
 *
 * @param props - The component props
 */
function renderPrefix(props) {
  // @ts-expect-error The `PrefixIconComponent` and `prefixText` props are accepted simultaneously
  const {
    PrefixIconComponent,
    prefixText
  } = props; // eslint-disable-line @typescript-eslint/naming-convention

  if (typeof PrefixIconComponent === 'undefined' && typeof prefixText !== 'string') {
    return null;
  }
  return /*#__PURE__*/jsx("span", {
    className: inputPrefixClassName,
    children: typeof PrefixIconComponent !== 'undefined' ? /*#__PURE__*/jsx(PrefixIconComponent, {}) : /*#__PURE__*/jsx("span", {
      "aria-hidden": "true",
      className: inputTextPrefixWrapperClassName,
      children: prefixText
    })
  });
}

/**
 * Renders an input field for text or numeric types.
 *
 * *The `Input` component needs to be a descendent of a `Form` component and its name must match one of its configured fields.*
 *
 * *Different props will be accepted epending on the value of `type`.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Input } from '@thisisbud/bui-react-input'
 *
 * function Example() {
 *   return <Input id='example' name='example' type='email'/>
 * }
 * ```
 */
function Input(props) {
  var _ref;
  // eslint-disable-line max-lines-per-function
  const {
    // @ts-expect-error The `PrefixIconComponent` is not compatible with the `prefixText` prop
    PrefixIconComponent,
    // eslint-disable-line @typescript-eslint/naming-convention
    autoComplete,
    className,
    describedBy,
    disabled,
    error,
    id,
    inputMode,
    list,
    max,
    maxLength,
    min,
    minLength,
    name,
    onBlur,
    onChange,
    onFocus,
    pattern,
    // @ts-expect-error The `prefixText` is not compatible with the `PrefixIconComponent` prop
    prefixText,
    readOnly,
    required,
    step,
    type
  } = props;
  const [field, meta, helpers] = useField({
    name: name,
    type: 'text'
  });
  const {
    setError,
    setValue
  } = helpers;
  const computedError = Boolean(error !== null && error !== void 0 ? error : meta.error) && !disabled; // eslint-disable-line @typescript-eslint/strict-boolean-expressions
  const computedInputMode = (_ref = inputMode) !== null && _ref !== void 0 ? _ref : getDefaultInputMode(type);

  // Allow using onBlur event listeners
  const onBlurHandler = React.useCallback(function (e) {
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    field.onBlur(e);
  }, [field, onBlur]);

  // Allow using onChange event listeners
  const onChangeHandler = React.useCallback(function (e) {
    if (typeof onChange === 'function') {
      onChange(e);
    }

    // Prevent value changes if the default is prevented
    if (!e.defaultPrevented) {
      // Clear existing error
      if (typeof meta.error === 'string') {
        setError(undefined);
      }
      const target = e.currentTarget;
      setValue(target.value);
    }
  }, [meta, onChange, setError, setValue]);

  // Update the left padding when a prefix is present
  React.useLayoutEffect(function () {
    const inputElement = document.getElementById(id); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const prefixElement = inputElement.parentElement.querySelector(`.${inputPrefixClassName}`); // eslint-disable-line @typescript-eslint/no-non-null-assertion

    if (prefixElement === null) {
      inputElement.style.paddingLeft = '';
      return;
    }
    inputElement.style.paddingLeft = rem(prefixElement.clientWidth);
  }, [PrefixIconComponent, id, prefixText]);
  return /*#__PURE__*/jsxs(StyledWrapper, {
    className: classnames(inputClassName, className, {
      [inputErrorStateClassName]: computedError
    }),
    children: [/*#__PURE__*/jsx("input", {
      "aria-describedby": describedBy,
      autoComplete: autoComplete,
      className: inputFieldClassName,
      disabled: disabled,
      id: id,
      inputMode: computedInputMode,
      list: list,
      max: max,
      maxLength: maxLength,
      min: min,
      minLength: minLength,
      name: name,
      pattern: pattern,
      readOnly: readOnly,
      required: required,
      step: step,
      type: type,
      value: field.value,
      onBlur: onBlurHandler,
      onChange: onChangeHandler,
      onFocus: onFocus
    }), renderPrefix(props)]
  });
}
Input.defaultProps = {
  type: 'text'
};
Input.displayName = 'Input';

export { Input, inputClassName };
